import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DialogFooter } from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { AddressAutocomplete } from '../maps/AddressAutocomplete';
import {
  Plus,
  Pill,
  Loader2,
  XCircle,
  Package,
  MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const OrderForm = ({ patients, services, medications, onSubmit, onClose }) => {
  const { t } = useTranslation('orders');
  
  const frequencyTypes = [
    { value: 'once', label: t('frequency.once'), placeholder: t('frequency.oncePlaceholder') },
    { value: 'hourly', label: t('frequency.hourly'), placeholder: t('frequency.hourlyPlaceholder') },
    { value: 'daily', label: t('frequency.daily'), placeholder: t('frequency.dailyPlaceholder') },
    { value: 'weekly', label: t('frequency.weekly'), placeholder: t('frequency.weeklyPlaceholder') },
    { value: 'monthly', label: t('frequency.monthly'), placeholder: t('frequency.monthlyPlaceholder') }
  ];

  const [formData, setFormData] = useState({
    patient_id: '',
    services: [],
    medications: [],
    priority: 'normal',
    scheduled_date: '',
    scheduled_time: '',
    service_address: '',
    service_city: '',
    service_latitude: null,
    service_longitude: null,
    diagnosis: '',
    observations: ''
  });
  const [loading, setLoading] = useState(false);
  const [usePatientAddress, setUsePatientAddress] = useState(true);

  const addService = () => {
    setFormData({
      ...formData,
      services: [...formData.services, { 
        service_id: '', 
        quantity: 1, 
        frequency_type: '',
        frequency_detail: '',
        notes: '' 
      }]
    });
  };

  const removeService = (index) => {
    const newServices = formData.services.filter((_, i) => i !== index);
    setFormData({ ...formData, services: newServices });
  };

  const updateService = async (index, field, value) => {
    const newServices = [...formData.services];
    newServices[index] = { ...newServices[index], [field]: value };
    
    if (field === 'service_id' && value) {
      try {
        const response = await axios.get(`${API_URL}/api/services/${value}/medications`);
        const serviceMeds = response.data.medications || [];
        
        if (serviceMeds.length > 0) {
          const newMedications = [...formData.medications];
          
          serviceMeds.forEach(sm => {
            const exists = newMedications.some(m => m.medication_id === sm.medication_id);
            if (!exists) {
              newMedications.push({
                medication_id: sm.medication_id,
                dose: sm.medication?.concentration || '',
                route: 'oral',
                frequency: sm.frequency || '',
                quantity: sm.quantity || 1,
                instructions: sm.instructions || '',
                auto_added: true
              });
            }
          });
          
          setFormData({ ...formData, services: newServices, medications: newMedications });
          
          if (serviceMeds.length > 0) {
            toast.success(`${serviceMeds.length} ${t('common:medications', 'medicamento(s)')} ${t('common:added', 'agregado(s)')}`);
          }
          return;
        }
      } catch (error) {
        console.error('Error fetching service medications:', error);
      }
    }
    
    setFormData({ ...formData, services: newServices });
  };

  const addMedication = () => {
    setFormData({
      ...formData,
      medications: [...formData.medications, { 
        medication_id: '', 
        dose: '',
        route: 'oral',
        frequency: '',
        quantity: 1,
        instructions: ''
      }]
    });
  };

  const removeMedication = (index) => {
    const newMedications = formData.medications.filter((_, i) => i !== index);
    setFormData({ ...formData, medications: newMedications });
  };

  const updateMedication = (index, field, value) => {
    const newMedications = [...formData.medications];
    newMedications[index] = { ...newMedications[index], [field]: value };
    setFormData({ ...formData, medications: newMedications });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.services.length === 0) {
      toast.error(t('form.validation.servicesRequired'));
      return;
    }
    
    const invalidServices = formData.services.some(s => !s.service_id);
    if (invalidServices) {
      toast.error(t('form.validation.serviceTypeRequired'));
      return;
    }
    
    setLoading(true);
    try {
      const cleanedData = {
        ...formData,
        scheduled_date: formData.scheduled_date || null,
        scheduled_time: formData.scheduled_time || null,
        service_address: formData.service_address || null,
        service_city: formData.service_city || null,
        service_latitude: formData.service_latitude || null,
        service_longitude: formData.service_longitude || null,
        diagnosis: formData.diagnosis || null,
        observations: formData.observations || null,
        services: formData.services.map(s => {
          const freqType = frequencyTypes.find(f => f.value === s.frequency_type);
          const frequency = s.frequency_type 
            ? `${freqType?.label || s.frequency_type}${s.frequency_detail ? ': ' + s.frequency_detail : ''}`
            : null;
          return {
            service_id: s.service_id,
            quantity: s.quantity,
            frequency: frequency,
            frequency_type: s.frequency_type || null,
            frequency_detail: s.frequency_detail || null,
            notes: s.notes || null
          };
        }),
        medications: formData.medications.map(m => ({
          ...m,
          frequency: m.frequency || null,
          instructions: m.instructions || null
        }))
      };
      await onSubmit(cleanedData);
      onClose();
    } catch (error) {
      console.error('Error saving order:', error);
    }
    setLoading(false);
  };

  const getSelectedService = (serviceId) => {
    return services.find(s => s.id === serviceId);
  };

  const getSelectedMedication = (medId) => {
    return medications.find(m => m.medication_id === medId);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>{t('form.patient')} *</Label>
        <Select
          value={formData.patient_id}
          onValueChange={(value) => setFormData({...formData, patient_id: value})}
        >
          <SelectTrigger data-testid="order-patient-select">
            <SelectValue placeholder={t('form.selectPatient')} />
          </SelectTrigger>
          <SelectContent>
            {patients.map((patient) => (
              <SelectItem key={patient.id} value={patient.id}>
                {patient.first_name} {patient.last_name} - {patient.document_number}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Services Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            {t('form.services')} *
          </Label>
          <Button type="button" variant="outline" size="sm" onClick={addService} data-testid="add-service-btn">
            <Plus className="h-4 w-4 mr-1" />
            {t('form.addService')}
          </Button>
        </div>
        
        {formData.services.length === 0 && (
          <div className="text-sm text-muted-foreground p-4 bg-muted rounded-lg text-center">
            {t('form.noServices')}
          </div>
        )}
        
        {formData.services.map((service, idx) => (
          <div key={idx} className="p-3 bg-muted rounded-lg space-y-2 border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">{t('common:service', 'Servicio')} {idx + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive h-6"
                onClick={() => removeService(idx)}
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
            <Select
              value={service.service_id}
              onValueChange={(value) => updateService(idx, 'service_id', value)}
            >
              <SelectTrigger data-testid={`service-select-${idx}`}>
                <SelectValue placeholder={t('form.selectService')} />
              </SelectTrigger>
              <SelectContent>
                {services.filter(s => s.status === 'active').map((svc) => (
                  <SelectItem key={svc.id} value={svc.id}>
                    {svc.name} - ${svc.base_price?.toLocaleString() || 0}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {service.service_id && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">{t('form.serviceQuantity')}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={service.quantity}
                      onChange={(e) => updateService(idx, 'quantity', parseInt(e.target.value) || 1)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('form.serviceFrequency')}</Label>
                    <Select
                      value={service.frequency_type || ''}
                      onValueChange={(value) => updateService(idx, 'frequency_type', value)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder={t('common:select', 'Seleccionar')} />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyTypes.map((freq) => (
                          <SelectItem key={freq.value} value={freq.value}>
                            {freq.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {service.frequency_type && (
                  <div>
                    <Label className="text-xs">{t('form.frequencyDetail')}</Label>
                    <Input
                      placeholder={frequencyTypes.find(f => f.value === service.frequency_type)?.placeholder || ''}
                      value={service.frequency_detail || ''}
                      onChange={(e) => updateService(idx, 'frequency_detail', e.target.value)}
                      className="h-8"
                    />
                  </div>
                )}
              </>
            )}
            
            {service.service_id && getSelectedService(service.service_id) && (
              <div className="text-xs text-muted-foreground">
                {t('common:code', 'Codigo')}: {getSelectedService(service.service_id).code} | 
                {t('common:unitPrice', 'Precio unitario')}: ${getSelectedService(service.service_id).base_price?.toLocaleString() || 0}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Medications Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <Pill className="h-4 w-4" />
            {t('form.medicationsOptional')}
          </Label>
          <Button type="button" variant="outline" size="sm" onClick={addMedication} data-testid="add-medication-btn">
            <Plus className="h-4 w-4 mr-1" />
            {t('form.addMedication')}
          </Button>
        </div>
        
        {formData.medications.length === 0 && (
          <div className="text-sm text-muted-foreground p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
            {t('form.noMedications')}
          </div>
        )}
        
        {formData.medications.map((med, idx) => (
          <div key={idx} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg space-y-2 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{t('common:medication', 'Medicamento')} {idx + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive h-6"
                onClick={() => removeMedication(idx)}
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
            <Select
              value={med.medication_id}
              onValueChange={(value) => updateMedication(idx, 'medication_id', value)}
            >
              <SelectTrigger data-testid={`medication-select-${idx}`}>
                <SelectValue placeholder={t('form.selectMedication')} />
              </SelectTrigger>
              <SelectContent>
                {medications.map((medication) => (
                  <SelectItem key={medication.medication_id} value={medication.medication_id}>
                    {medication.medication_name} - Stock: {medication.total_stock} {medication.unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {med.medication_id && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">{t('form.dose')} *</Label>
                    <Input
                      placeholder="ej: 500mg"
                      value={med.dose}
                      onChange={(e) => updateMedication(idx, 'dose', e.target.value)}
                      className="h-8"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('form.route')}</Label>
                    <Select
                      value={med.route || 'oral'}
                      onValueChange={(value) => updateMedication(idx, 'route', value)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="oral">{t('routes.oral')}</SelectItem>
                        <SelectItem value="iv">{t('routes.iv')}</SelectItem>
                        <SelectItem value="im">{t('routes.im')}</SelectItem>
                        <SelectItem value="sc">{t('routes.sc')}</SelectItem>
                        <SelectItem value="topical">{t('routes.topical')}</SelectItem>
                        <SelectItem value="inhalation">{t('routes.inhalation')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{t('common:quantity', 'Cantidad')}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={med.quantity}
                      onChange={(e) => updateMedication(idx, 'quantity', parseInt(e.target.value) || 1)}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">{t('common:frequency', 'Frecuencia')}</Label>
                    <Input
                      placeholder="ej: cada 8 horas"
                      value={med.frequency || ''}
                      onChange={(e) => updateMedication(idx, 'frequency', e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('form.instructions')}</Label>
                    <Input
                      placeholder="ej: con alimentos"
                      value={med.instructions || ''}
                      onChange={(e) => updateMedication(idx, 'instructions', e.target.value)}
                      className="h-8"
                    />
                  </div>
                </div>
                {getSelectedMedication(med.medication_id) && (
                  <div className="text-xs text-blue-600 dark:text-blue-400">
                    Stock: {getSelectedMedication(med.medication_id).total_stock} {getSelectedMedication(med.medication_id).unit}
                    {getSelectedMedication(med.medication_id).is_low_stock && (
                      <span className="text-orange-500 ml-2">({t('common:lowStock', 'Stock bajo')})</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Service Address Section */}
      <div className="space-y-3 p-3 bg-muted rounded-lg border">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            {t('form.serviceAddress')}
          </Label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={usePatientAddress}
              onChange={(e) => {
                setUsePatientAddress(e.target.checked);
                if (e.target.checked && formData.patient_id) {
                  const patient = patients.find(p => p.id === formData.patient_id);
                  if (patient?.address) {
                    setFormData({
                      ...formData,
                      service_address: patient.address.street || '',
                      service_city: patient.address.city || '',
                      service_latitude: null,
                      service_longitude: null
                    });
                  }
                }
              }}
              className="rounded border-gray-300"
            />
            {t('form.usePatientAddress')}
          </label>
        </div>
        
        {!usePatientAddress && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">{t('form.searchAddress')}</Label>
              <AddressAutocomplete
                value={formData.service_address}
                onChange={(value) => setFormData({...formData, service_address: value})}
                onSelect={(place) => {
                  setFormData({
                    ...formData,
                    service_address: place.formatted_address || place.address,
                    service_city: place.city || '',
                    service_latitude: place.latitude,
                    service_longitude: place.longitude
                  });
                }}
                placeholder={t('form.addressPlaceholder')}
                data-testid="order-service-address"
              />
            </div>
            {formData.service_latitude && (
              <p className="text-xs text-green-600 dark:text-green-400">
                {t('form.coordinates')}: {formData.service_latitude?.toFixed(6)}, {formData.service_longitude?.toFixed(6)}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('form.priority')}</Label>
          <Select
            value={formData.priority}
            onValueChange={(value) => setFormData({...formData, priority: value})}
          >
            <SelectTrigger data-testid="order-priority-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">{t('priority.low')}</SelectItem>
              <SelectItem value="normal">{t('priority.normal')}</SelectItem>
              <SelectItem value="high">{t('priority.high')}</SelectItem>
              <SelectItem value="urgent">{t('priority.urgent')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('form.scheduledDate')}</Label>
          <Input
            type="date"
            value={formData.scheduled_date}
            onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
            data-testid="order-scheduled-date"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t('form.diagnosis')}</Label>
        <Textarea
          value={formData.diagnosis}
          onChange={(e) => setFormData({...formData, diagnosis: e.target.value})}
          placeholder={t('form.diagnosisPlaceholder')}
          data-testid="order-diagnosis"
        />
      </div>

      <div className="space-y-2">
        <Label>{t('form.observations')}</Label>
        <Textarea
          value={formData.observations}
          onChange={(e) => setFormData({...formData, observations: e.target.value})}
          placeholder={t('form.observationsPlaceholder')}
          data-testid="order-observations"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common:cancel', 'Cancelar')}
        </Button>
        <Button 
          type="submit" 
          className="bg-primary hover:bg-primary/90" 
          disabled={loading || !formData.patient_id || formData.services.length === 0}
          data-testid="create-order-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {t('form.create')}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default OrderForm;
